/**
 * WorkstationHardwareList — el hardware que reporta el agente, en la pestaña
 * Hardware de la ficha.
 *
 * Antes había que abrir el modal «Componentes» para ver qué lleva dentro el
 * equipo. Es la misma información de `GET /workstations/<id>/components`, pero
 * en solo lectura y siempre visible: en el modal se edita, aquí se consulta.
 */

import React, { useMemo } from 'react';
import {
  BoltIcon,
  CircleStackIcon,
  CpuChipIcon,
  FireIcon,
  PuzzlePieceIcon,
  RectangleStackIcon,
  ServerStackIcon,
  SignalIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

const TYPE_LABEL = {
  cpu: 'Procesador',
  ram: 'Memoria',
  disk: 'Disco',
  psu: 'Fuente',
  nic: 'Tarjeta de red',
  hba: 'HBA',
  gpu: 'Gráfica',
  raid: 'RAID',
  fan: 'Ventilador',
  battery: 'Batería',
  other: 'Otro',
};

const TYPE_META = {
  cpu: { label: 'Procesador', Icon: CpuChipIcon },
  ram: { label: 'Memoria', Icon: Squares2X2Icon },
  disk: { label: 'Disco', Icon: CircleStackIcon },
  gpu: { label: 'Gráfica', Icon: RectangleStackIcon },
  nic: { label: 'Tarjeta de red', Icon: SignalIcon },
  hba: { label: 'HBA', Icon: ServerStackIcon },
  raid: { label: 'RAID', Icon: PuzzlePieceIcon },
  psu: { label: 'Fuente', Icon: BoltIcon },
  fan: { label: 'Ventilador', Icon: FireIcon },
  battery: { label: 'Batería', Icon: BoltIcon },
};

const STATUS_LABEL = {
  ok: 'Correcto',
  warning: 'Aviso',
  failed: 'Averiado',
  unknown: 'Sin datos',
};

/** Resume `notes` (JSON del agente) en una línea legible. */
function describe(comp) {
  let notas = {};
  try {
    notas = JSON.parse(comp.notes || '{}') || {};
  } catch {
    notas = {};
  }
  const partes = [];
  if (notas.capacity_gb) partes.push(`${notas.capacity_gb} GB`);
  if (notas.type) partes.push(notas.type);
  if (notas.speed_mhz) partes.push(`${notas.speed_mhz} MHz`);
  if (notas.cores) partes.push(`${notas.cores}C/${notas.threads || notas.cores}T`);
  if (notas.ecc) partes.push('ECC');
  if (comp.power_watts) partes.push(`${comp.power_watts} W`);
  if (comp.connector_type) partes.push(comp.connector_type);
  if (notas.temperature_c) partes.push(`${notas.temperature_c} °C`);
  return partes.join(' · ');
}

export default function WorkstationHardwareList({ components }) {
  const items = Array.isArray(components) ? components : [];

  // Agrupados por tipo en el orden natural de un equipo, no alfabético.
  const grupos = useMemo(() => {
    const order = ['cpu', 'ram', 'disk', 'gpu', 'nic', 'hba', 'raid', 'psu', 'fan', 'battery'];
    const mapa = new Map();
    items.forEach((comp) => {
      const tipo = comp.component_type || 'other';
      if (!mapa.has(tipo)) mapa.set(tipo, []);
      mapa.get(tipo).push(comp);
    });
    return [...mapa.entries()].sort((a, b) => {
      const ia = order.indexOf(a[0]);
      const ib = order.indexOf(b[0]);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="dv-section">
        <h3 className="dv-section-title" style={{ textAlign: 'left' }}>
          Componentes detectados
        </h3>
        <p className="sf2-hint" style={{ margin: 0 }}>
          El agente todavía no ha reportado componentes de este equipo. Un
          «Rescan completo», en la pestaña Agente, los vuelve a pedir.
        </p>
      </div>
    );
  }

  return (
    <div className="dv-section">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 10,
          justifyContent: 'flex-start',
          textAlign: 'left',
        }}
      >
        <h3 className="dv-section-title" style={{ margin: 0, textAlign: 'left' }}>
          Componentes detectados
        </h3>
        <span className="sf2-pill sf2-pill--ok">
          {items.length} componente{items.length === 1 ? '' : 's'}
        </span>
        <span className="sf2-hint" style={{ margin: 0 }}>
          Detectado por el agente. Para añadir o corregir, usa «Componentes».
        </span>
      </div>

      {grupos.map(([tipo, grupo]) => {
        const meta = TYPE_META[tipo] || { label: tipo, Icon: PuzzlePieceIcon };
        const Icon = meta.Icon;
        return (
        <div className="sf2-card" key={tipo} style={{ marginBottom: 10 }}>
          <h3
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'flex-start',
              textAlign: 'left',
              marginTop: 0,
            }}
          >
            <Icon style={{ width: 16, height: 16, flexShrink: 0 }} aria-hidden="true" />
            <span>{meta.label}</span>
            <span className="sf2-pill">{grupo.length}</span>
          </h3>
          <div className="sf2-table-wrap">
            <table className="sf2-table">
              <thead>
                <tr>
                  <th>Slot</th>
                  <th>Marca / modelo</th>
                  <th>Nº de serie</th>
                  <th>Detalle</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {grupo.map((comp) => (
                  <tr key={comp.id}>
                    <td>
                      {comp.slot ? <code>{comp.slot}</code> : <span className="sf2-hint">—</span>}
                    </td>
                    <td>
                      {[comp.manufacturer, comp.model].filter(Boolean).join(' ') || (
                        <span className="sf2-hint">—</span>
                      )}
                    </td>
                    <td>
                      {comp.serial_number ? (
                        <code>{comp.serial_number}</code>
                      ) : (
                        <span className="sf2-hint">—</span>
                      )}
                    </td>
                    <td>{describe(comp) || <span className="sf2-hint">—</span>}</td>
                    <td>
                      <span
                        className={`sf2-pill ${
                          comp.status === 'ok'
                            ? 'sf2-pill--ok'
                            : comp.status === 'failed'
                              ? 'sf2-pill--bad'
                              : comp.status === 'warning'
                                ? 'sf2-pill--wa'
                                : ''
                        }`}
                      >
                        {STATUS_LABEL[comp.status] || comp.status || 'Sin datos'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        );
      })}
    </div>
  );
}
